import { io, Socket } from 'socket.io-client';
import { useMarketStore } from '../store/marketStore';
import { useNewsStore, NewsHeadline, CalendarEvent } from '../store/newsStore';
import { useSignalPredictorStore, SignalPrediction } from '../store/signalPredictorStore';
import { useBrokerStore, BrokerState, BrokerAccount, CopyTradingConfig, CopyTradeEvent, CopyTradeActivity, initBrokerSocketHandlers, resetBrokerSocketHandlers } from '../store/brokerStore';
import { isHiddenBrokerId } from '../store/brokerVisibility';
import { initAccountStatsHandlers, resetAccountStatsSocketHandlers } from '../store/accountStatsStore';
import { useToastStore, initToastHistoryListener, setWsServiceForToast, resetToastSocketHandlers } from '../store/toastStore';
import { useDisciplineStore, initDisciplineSocketHandlers, resetDisciplineSocketHandlers } from '../store/disciplineStore';
import { initSRSocketHandlers, resetSRSocketHandlers } from '../store/srStore';
import { initAutoBeSocketHandlers, resetAutoBeSocketHandlers, fetchAutoBeState } from '../store/autoBeStore';
import { initRestrainSocketHandlers, resetRestrainSocketHandlers, requestRestrainState } from '../store/restrainStore';
import { initVestVolumeSocketHandlers, resetVestVolumeSocketHandlers } from '../store/vestVolumeStore';
import { initVestPositionSocketHandlers, resetVestPositionSocketHandlers } from '../store/vestPositionsStore';
import { initVestTpslSocketHandlers, resetVestTpslSocketHandlers } from '../store/vestTpslStore';
import { initVestSizingSocketHandlers, resetVestSizingSocketHandlers } from '../store/vestSizingStore';
import { initMesCandleFeedSocketHandlers, resetMesCandleFeedSocketHandlers } from '../store/mesCandleFeedStore';
import { useSymbolStore } from '../store/symbolStore';
import { normalizeToBase } from '../utils/symbolUtils';
import { useSettingsStore } from '../store/settingsStore';
import { useVolumeProfileStore } from '../store/volumeProfileStore';
import { useProfileLevelsStore, ProfileLevels } from '../store/profileLevelsStore';
import { FootprintData, DeltaData, VolumeProfileSnapshot } from '../types/volumeProfile';
import { Candle, SignalMarker, ICTSettings, DEFAULT_ICT_SETTINGS } from '@shared/types';
import { useDonchianStore, DonchianMarker } from '../store/donchianStore';
import { useCandidateStore, type EngineDiamond } from '../store/candidateStore';
import { useBosStore, BosMarker } from '../store/bosStore';
import { getBacktestPlayer } from './BacktestPlayer';
import { CONFIG } from '../config';
import { logger } from './Logger';

const log = logger.child('WebSocket');

/**
 * Base symbol of a full contract symbol (MNQH6.CME -> MNQ).
 *
 * Delegates to the one canonical normalizer. The local copy this replaced
 * ended with `return withoutExchange.replace(...) || upper` -- a fallback that
 * handed back a CONTRACT-BEARING symbol whenever the registry had no matching
 * base, which is precisely the state the app is in mid-rollover. Marker
 * de-duplication then compared a base against a full symbol and never matched.
 */
const normalizeSymbol = (sym: string | undefined): string => normalizeToBase(sym || '');

// Trailing OHLC data for the final N seconds of a candle
interface TrailingOHLC {
  open: number;
  high: number;
  low: number;
  close: number;
  durationSeconds: number;
  isValid: boolean;
}

interface MarketDataUpdate {
  symbol: string;
  candle: Candle;
  isNewCandle: boolean;
  trailing?: TrailingOHLC;
  // Pre-calculated indicators from backend
  ema9?: number;
  ema21?: number;
  ema200?: number;
  vwap?: number;
}

// Backend sends CandleWithTrailing with candle fields flattened + optional trailing + pre-calculated indicators
interface CandleWithTrailing {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  _isCompleted?: boolean;
  trailing?: TrailingOHLC;
  // Pre-calculated indicators from backend
  ema9?: number;
  ema21?: number;
  ema200?: number;
  vwap?: number;
}

interface HistoricalDataResponse {
  symbol: string;
  candles: CandleWithTrailing[];
}

interface ServerStatus {
  symbols: string[];
  availableSymbols: string[];
  connections: number;
  uptime: number;
}

interface SubscribeOptions {
  skipHistorical?: boolean;
  /**
   * Chart an expired contract on purpose.
   *
   * Without this the engine re-maps any request whose base it is configured to
   * stream onto the LIVE contract, because a bare `NQM6.CME` on the wire is
   * indistinguishable from a stale one -- and stale is overwhelmingly the
   * likelier sender. Setting it is the caller saying "I meant that contract".
   */
  allowExpired?: boolean;
}

/**
 * WebSocket Service for real-time market data
 * Manages Socket.IO connection to backend server
 */
/**
 * Events that can open, close, or resize a position.
 *
 * Mirrors `order_log::MUTATING` in utt-engine. If an order-placing event is
 * added on one side and not the other it ships unattributed, which is the
 * failure this whole mechanism exists to prevent.
 */
const POSITION_MOVING_EVENTS = new Set([
  'broker:place-order',
  'broker:place-bracket-order',
  'broker:modify-order',
  'broker:modify-order-quantity',
  'broker:cancel-order',
  'broker:replace-bracket',
  'broker:split-order',
  'broker:flatten',
]);

let orderOriginSeq = 0;

/**
 * Everything we can cheaply learn about who is asking for an order.
 *
 * `userGesture` is the load-bearing field. `navigator.userActivation.isActive`
 * is true only inside the window following a real user interaction, so it
 * separates "a human clicked this" from "code decided this on its own" —
 * exactly the question nobody could answer about the 02:09 burst. `stack`
 * names the component; `visibility` catches a background tab acting on its own.
 */
function captureOrderOrigin(event: string) {
  const stack = (new Error().stack || '')
    .split('\n')
    .slice(2)
    .map((l) => l.trim().replace(/^at\s+/, ''))
    .filter((l) => l && !l.includes('WebSocketService'))
    .slice(0, 6)
    .join(' < ');

  const activation = (navigator as any).userActivation;

  return {
    id: `ord_${Date.now().toString(36)}_${(++orderOriginSeq).toString(36)}`,
    event,
    at: new Date().toISOString(),
    url: typeof location !== 'undefined' ? location.pathname + location.search : '-',
    visibility: typeof document !== 'undefined' ? document.visibilityState : '-',
    // null when the browser does not expose it, so "unknown" is never mistaken
    // for "no gesture".
    userGesture: activation ? Boolean(activation.isActive) : null,
    everInteracted: activation ? Boolean(activation.hasBeenActive) : null,
    stack,
  };
}

export class WebSocketService {
  private socket: Socket | null = null;
  private serverUrl: string;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = CONFIG.websocket.reconnectionAttempts;
  private baseReconnectDelay: number = CONFIG.websocket.reconnectionDelay;
  private maxReconnectDelay: number = 30000; // 30 seconds
  private subscribedSymbols: Set<string> = new Set();
  private pendingSubscriptions: Set<string> = new Set();
  private pendingSeekTimestamp?: number;
  private backtestChunkBuffer: any[] = [];
  private expectedTotalChunks: number = 0;
  private visibilityHandlerBound: (() => void) | null = null;
  private pageResumeHandlerBound: (() => void) | null = null;
  private lastVisibilityRefresh: number = 0;
  private readonly VISIBILITY_REFRESH_DEBOUNCE_MS = CONFIG.ui.debounceMs;

  // Track when page went hidden for smart catch-up
  private hiddenTimestamp: number = 0;
  private lastCandleTimestamp: number = 0;
  private lastMarkerTimestamp: number = 0;
  // Snapshot of lastCandleTimestamp at reconnect time (not updated by live data)
  private catchUpSinceTimestamp: number = 0;

  // Heartbeat mechanism to detect dead connections
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private lastPongTime: number = 0;
  private readonly HEARTBEAT_INTERVAL_MS = 30000; // 30 seconds
  private readonly HEARTBEAT_TIMEOUT_MS = 10000; // 10 seconds to respond
  private onlineHandlerBound: (() => void) | null = null;
  private hasCompletedInitialConnect: boolean = false;

  // Marker sync mechanism to detect stale data
  private markerSyncInterval: ReturnType<typeof setInterval> | null = null;
  private readonly MARKER_SYNC_INTERVAL_MS = 30000; // 30 seconds


  constructor(serverUrl?: string) {
    // Auto-detect server URL based on current hostname using centralized config
    // IMPORTANT: Use CONFIG.getBackendUrl() (runtime) not CONFIG.api.backend (build-time frozen)
    if (!serverUrl) {
      this.serverUrl = CONFIG.getBackendUrl();
    } else {
      this.serverUrl = serverUrl;
    }
    log.info('Server URL configured', { serverUrl: this.serverUrl });
  }

  /**
   * Calculate reconnection delay with exponential backoff and jitter
   */
  private getReconnectDelay(): number {
    // Exponential backoff: delay = baseDelay * 2^attempts
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    );
    // Add jitter (±25%) to avoid thundering herd
    const jitter = delay * 0.25 * (Math.random() - 0.5);
    return Math.round(delay + jitter);
  }

  /**
   * Connect to WebSocket server
   */
  connect(): void {
    if (this.socket?.connected) {
      log.info('Already connected to server');
      return;
    }

    log.info('Connecting to server', { serverUrl: this.serverUrl });
    console.log('🔌 [WS] connect() called', {
      serverUrl: this.serverUrl,
      subscribedSymbols: Array.from(this.subscribedSymbols),
      pendingSubscriptions: Array.from(this.pendingSubscriptions),
      hasSocket: !!this.socket,
    });

    this.socket = io(this.serverUrl, {
      reconnection: true,
      reconnectionDelay: this.baseReconnectDelay,
      reconnectionDelayMax: this.maxReconnectDelay,
      reconnectionAttempts: this.maxReconnectAttempts,
      randomizationFactor: 0.25, // Adds jitter to reconnection delays
      timeout: 20000,
      transports: ['websocket'], // Force WebSocket only (no polling)
      withCredentials: true, // Include cookies for session authentication
    });

    this.setupEventHandlers();
    this.setupVisibilityHandler();
    this.setupOnlineHandler();
    this.startHeartbeat();
    this.startMarkerSync();

    // Initialize broker socket handlers for multi-broker support
    initBrokerSocketHandlers();
    // Vest positions + TP/SL for the .VEST chart lines (#636), kept out of brokerStore
    initVestPositionSocketHandlers();
    initVestTpslSocketHandlers();
    // Vest ATR TP/SL + size suggestions for .VEST charts (#637), also kept out of brokerStore
    initVestSizingSocketHandlers();

    // Initialize account stats socket handlers
    initAccountStatsHandlers();

    // Initialize S/R socket handlers
    initSRSocketHandlers();

    // Auto break-even: shared setting + engine fired/failed broadcasts
    initAutoBeSocketHandlers();

    // Initialize toast history socket handlers
    // Must set wsService instance first to break circular dependency
    setWsServiceForToast(this);
    initToastHistoryListener();

    // Initialize discipline socket handlers for RMS discipline system
    initDisciplineSocketHandlers(this);

    // Restrain (position-size freeze): rms:restrain + RESTRAIN_ACTIVE refusals
    initRestrainSocketHandlers(this);
  }

  /**
   * Setup page visibility change handler for mobile browser tab switching
   * When page regains focus, perform smart catch-up for missed data
   */
  private setupVisibilityHandler(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;

    // Remove existing handler if any
    if (this.visibilityHandlerBound) {
      document.removeEventListener('visibilitychange', this.visibilityHandlerBound);
    }
    if (this.pageResumeHandlerBound) {
      window.removeEventListener('focus', this.pageResumeHandlerBound);
      window.removeEventListener('pageshow', this.pageResumeHandlerBound);
    }

    this.visibilityHandlerBound = () => {
      if (document.visibilityState === 'hidden') {
        this.handlePageHidden();
      } else if (document.visibilityState === 'visible') {
        this.handlePageVisible();
      }
    };

    this.pageResumeHandlerBound = () => {
      if (document.visibilityState === 'visible') {
        this.handlePageVisible();
      }
    };

    document.addEventListener('visibilitychange', this.visibilityHandlerBound);
    window.addEventListener('focus', this.pageResumeHandlerBound);
    window.addEventListener('pageshow', this.pageResumeHandlerBound);
    log.debug('Visibility change handlers registered');
  }

  /**
   * Setup online/offline event handlers as backup for visibility change
   * navigator.onLine events can catch network restoration that visibility misses
   */
  private setupOnlineHandler(): void {
    if (typeof window === 'undefined') return;

    // Remove existing handler if any
    if (this.onlineHandlerBound) {
      window.removeEventListener('online', this.onlineHandlerBound);
    }

    this.onlineHandlerBound = () => {
      console.log('🌐 Network online detected - checking connections...');
      // Small delay to let network stabilize
      setTimeout(() => {
        this.checkAndReconnect();
      }, 1000);
    };

    window.addEventListener('online', this.onlineHandlerBound);
    log.debug('Online event handler registered');
  }

  /**
   * Start heartbeat interval to detect dead connections
   */
  private startHeartbeat(): void {
    this.stopHeartbeat(); // Clear any existing interval

    this.lastPongTime = Date.now();
    this.heartbeatInterval = setInterval(() => {
      if (!this.socket?.connected) {
        return; // Socket knows it's disconnected, no need to check
      }

      const now = Date.now();
      const timeSinceLastPong = now - this.lastPongTime;

      // If we haven't received a pong in too long, connection is likely dead
      if (timeSinceLastPong > this.HEARTBEAT_INTERVAL_MS + this.HEARTBEAT_TIMEOUT_MS) {
        console.log(`💔 Heartbeat timeout (${Math.round(timeSinceLastPong / 1000)}s since last pong) - forcing reconnect`);
        this.forceReconnect();
        return;
      }

      // Send ping
      this.socket.emit('ping', { timestamp: now });
    }, this.HEARTBEAT_INTERVAL_MS);

    log.debug('Heartbeat started');
  }

  /**
   * Stop heartbeat interval
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Fetch trailing pullback markers from the backend on initial connection
   */
  private async fetchTrailingPullbackMarkers(): Promise<void> {
    try {
      const marketStore = useMarketStore.getState();

      const response = await fetch(`${this.serverUrl}/api/trailing-pullback-markers`);
      if (response.ok) {
        const markers = await response.json();
        if (Array.isArray(markers)) {
          // Normalize snake_case to camelCase and merge with existing markers
          const normalizedMarkers = markers.map((m: any) => ({
            timestamp: m.timestamp,
            type: m.marker_type || m.type,
            symbol: m.symbol,
            confidence: m.confidence,
            classification: m.classification,
            trailingDirection: m.trailing_direction ?? m.trailingDirection,
            candleDirection: m.candle_direction ?? m.candleDirection,
            wccAligned: m.wcc_aligned ?? m.wccAligned,
            skipped: m.skipped,
            skipReason: m.skip_reason ?? m.skipReason,
          }));

          // Get existing non-trailing-pullback markers
          const existingMarkers = marketStore.signalMarkers.filter(
            m => m.type !== 'trailing_pullback_bullish' && m.type !== 'trailing_pullback_bearish'
          );
          marketStore.setSignalMarkers([...existingMarkers, ...normalizedMarkers]);
          console.log(`🎯 [TRAILING PULLBACK] Loaded ${normalizedMarkers.length} trailing pullback markers`);
        }
      }
    } catch (error) {
      console.warn('Failed to fetch trailing pullback markers:', error);
    }
  }

  /**
   * Fetch FVG zones from the backend on initial connection
   */
  private async fetchFVGZones(): Promise<void> {
    try {
      const marketStore = useMarketStore.getState();

      const response = await fetch(`${this.serverUrl}/api/fvg-zones`);
      if (response.ok) {
        const zones = await response.json();
        if (Array.isArray(zones)) {
          marketStore.setFVGZones(zones);
          console.log(`📦 [ICT] Loaded ${zones.length} FVG zones`);
        }
      }
    } catch (error) {
      console.warn('Failed to fetch FVG zones:', error);
    }
  }

  /**
   * Fetch ICT settings from the backend on initial connection
   */
  private async fetchICTSettings(): Promise<void> {
    try {
      const response = await fetch(`${this.serverUrl}/api/ict-settings`);
      if (response.ok) {
        const settings: ICTSettings = await response.json();
        const merged = { ...DEFAULT_ICT_SETTINGS, ...settings };
        useSettingsStore.getState().setICTSettings(merged);
        console.log(`📦 [ICT] Loaded settings: enabled=${merged.enabled}, showFvg=${merged.showFvgOnChart}, showIfvg=${merged.showIfvgOnChart}`);
      }
    } catch (error) {
      console.warn('Failed to fetch ICT settings:', error);
    }
  }

  /**
   * Start marker sync interval to detect and fix stale marker data
   */
  private startMarkerSync(): void {
    this.stopMarkerSync(); // Clear any existing interval

    this.markerSyncInterval = setInterval(async () => {
      if (!this.socket?.connected) {
        return; // Only sync when connected
      }

      try {
        const marketStore = useMarketStore.getState();
        const localVersion = marketStore.markersVersion;

        // Check backend version
        const response = await fetch(`${this.serverUrl}/api/signal-markers/version`);
        if (!response.ok) return;

        const data = await response.json();
        const serverVersion = data.version;

        // If versions differ, fetch fresh markers
        if (serverVersion !== localVersion && serverVersion > 0) {
          console.log(`🔄 [MARKER SYNC] Version mismatch: local=${localVersion}, server=${serverVersion} - fetching fresh markers`);

          const markersResponse = await fetch(`${this.serverUrl}/api/signal-markers?days=3`);
          if (markersResponse.ok) {
            const markersData = await markersResponse.json();
            if (markersData.markers) {
              console.log('🔄 [MARKER SYNC] Fetched marker payload', {
                count: markersData.markers.length,
                version: markersData.version,
              });
              // Preserve volume markers
              const existingVolumeMarkers = marketStore.signalMarkers.filter(
                m => m.type === 'volume_spike_bullish' || m.type === 'volume_spike_bearish'
              );
              // Preserve trailing pullback markers
              const existingTrailingPullbackMarkers = marketStore.signalMarkers.filter(
                m => m.type === 'trailing_pullback_bullish' || m.type === 'trailing_pullback_bearish'
              );
              marketStore.setSignalMarkers([...markersData.markers, ...existingVolumeMarkers, ...existingTrailingPullbackMarkers]);
              if (markersData.version !== undefined) {
                marketStore.setMarkersVersion(markersData.version);
              }
              console.log(`✅ [MARKER SYNC] Synced ${markersData.markers.length} markers, version=${markersData.version}`);
            }
          }
        }
      } catch (err) {
        // Silently ignore sync errors - will retry next interval
      }
    }, this.MARKER_SYNC_INTERVAL_MS);

    log.debug('Marker sync started');
  }

  /**
   * Stop marker sync interval
   */
  private stopMarkerSync(): void {
    if (this.markerSyncInterval) {
      clearInterval(this.markerSyncInterval);
      this.markerSyncInterval = null;
    }
  }

  /**
   * Check connection health and reconnect if needed
   * Called on visibility change and online events
   */
  private checkAndReconnect(): void {
    const now = Date.now();

    // Always check Tradovate connection first
    console.log('🌐 checkAndReconnect: checking Tradovate connection...');
    this.restoreTradovateConnection();

    // If socket claims to be connected, verify with a ping
    if (this.socket?.connected) {
      const timeSinceLastPong = now - this.lastPongTime;

      // If we haven't had a pong recently, the connection might be stale
      if (timeSinceLastPong > this.HEARTBEAT_INTERVAL_MS * 2) {
        console.log(`⚠️ Connection may be stale (${Math.round(timeSinceLastPong / 1000)}s since last pong) - forcing reconnect`);
        this.forceReconnect();
        return;
      }

      // Send immediate ping to verify
      this.socket.emit('ping', { timestamp: now });
      console.log('📶 Connection appears healthy, sent verification ping');
    } else {
      // Socket is disconnected, force reconnect
      console.log('🔌 Socket disconnected - forcing reconnect');
      this.forceReconnect();
    }
  }

  /**
   * Handle page becoming hidden (tab switch, minimize, lock screen)
   * Record timestamp for smart catch-up later
   */
  private handlePageHidden(): void {
    this.hiddenTimestamp = Date.now();

    // Capture current state for catch-up
    const marketStore = useMarketStore.getState();
    const candles = marketStore.candles;
    const markers = marketStore.signalMarkers;

    if (candles.length > 0) {
      this.lastCandleTimestamp = candles[candles.length - 1].timestamp;
    }
    if (markers.length > 0) {
      this.lastMarkerTimestamp = Math.max(...markers.map(m => m.timestamp));
    }

    log.debug('Page hidden - capturing state for catch-up', {
      hiddenAt: new Date(this.hiddenTimestamp).toLocaleTimeString(),
      lastCandle: new Date(this.lastCandleTimestamp).toLocaleTimeString(),
      lastMarker: new Date(this.lastMarkerTimestamp).toLocaleTimeString()
    });
  }

  /**
   * Handle page becoming visible again (e.g., mobile tab switch back)
   * Performs smart incremental catch-up instead of full data refresh
   * Forces reconnection if socket is disconnected after long background period
   */
  private handlePageVisible(): void {
    const now = Date.now();
    const marketStore = useMarketStore.getState();

    console.log(`👁️ [VISIBILITY] Page visible. Socket connected: ${this.socket?.connected}, subscribedSymbols: ${Array.from(this.subscribedSymbols).join(',')}, pendingSubscriptions: ${Array.from(this.pendingSubscriptions).join(',')}`);

    // Only refresh in live mode
    if (marketStore.dataMode !== 'live') {
      console.log('👁️ Page visible, but not in live mode - skipping refresh');
      return;
    }

    // Debounce rapid visibility changes
    if (now - this.lastVisibilityRefresh < this.VISIBILITY_REFRESH_DEBOUNCE_MS) {
      console.log('👁️ Page visible, but debouncing - skipping refresh');
      return;
    }

    // Calculate time hidden
    const timeHiddenMs = this.hiddenTimestamp > 0 ? now - this.hiddenTimestamp : 0;
    const timeHiddenSec = Math.round(timeHiddenMs / 1000);

    this.lastVisibilityRefresh = now;

    // Mobile browsers kill WebSockets unpredictably in background.
    // socket.connected can lie (reports true on a dead socket).
    // Be aggressive: on mobile, force reconnect after just 5s hidden.
    // On desktop, use 30s threshold with pong-based staleness check.
    const isMobile = window.innerWidth <= 768 || 'ontouchstart' in window;
    const mobileStaleThreshold = 5000;   // 5 seconds
    const desktopStaleThreshold = 30000; // 30 seconds
    const timeSinceLastPong = now - this.lastPongTime;
    const connectionMayBeStale = isMobile
      ? timeHiddenMs > mobileStaleThreshold
      : timeHiddenMs > desktopStaleThreshold && timeSinceLastPong > this.HEARTBEAT_INTERVAL_MS;

    // Always try to restore Tradovate connection when page becomes visible
    // Do this BEFORE checking main socket - Tradovate has its own socket
    console.log(`👁️ Page visible after ${timeHiddenSec}s hidden (${isMobile ? 'mobile' : 'desktop'}) - checking connections...`);
    this.restoreTradovateConnection();

    // Check if we need to force reconnect main socket
    if (!this.socket?.connected || connectionMayBeStale) {
      const reason = !this.socket?.connected ? 'socket disconnected'
        : isMobile ? `mobile hidden ${timeHiddenSec}s (>5s threshold)`
        : 'connection may be stale';
      console.log(`👁️ Main socket: ${reason}, forcing reconnect...`);

      // Force full reconnection - catch-up happens in 'connect' handler
      this.forceReconnect();
      return;
    }

    // Socket is connected - refresh broker data (positions/orders may have changed while hidden)
    console.log('👁️ Refreshing broker data after visibility change...');
    const brokerStore = useBrokerStore.getState();

    // Request updated broker list (which includes cached positions/orders from backend)
    this.socket?.emit('broker:list');

    // Also fetch positions and orders for all connected brokers
    const connectedBrokers = brokerStore.getConnectedBrokers();
    connectedBrokers.forEach(broker => {
      console.log(`👁️ Fetching positions/orders for broker ${broker.id}...`);
      brokerStore.fetchPositions(broker.id);
      brokerStore.fetchOrders(broker.id);
    });

    // Socket is connected, perform smart catch-up
    if (this.subscribedSymbols.size === 0) {
      console.log('👁️ Page visible, but no symbols subscribed - skipping market data refresh');
      return;
    }

    console.log(`👁️ Performing smart catch-up for market data...`);

    // Request catch-up for all subscribed symbols
    this.subscribedSymbols.forEach((symbol) => {
      // If we have a valid last candle timestamp, use smart catch-up
      if (this.lastCandleTimestamp > 0) {
        // Add 2-candle offset (4 minutes = 240,000ms) to catch edge cases
        const CANDLE_OFFSET_MS = 2 * 2 * 60 * 1000; // 2 candles × 2 min/candle × 60 sec × 1000 ms
        const adjustedTimestamp = this.lastCandleTimestamp - CANDLE_OFFSET_MS;
        console.log(`📥 Smart catch-up for ${symbol} since ${new Date(adjustedTimestamp).toLocaleTimeString()} (2-candle offset from ${new Date(this.lastCandleTimestamp).toLocaleTimeString()})`);
        this.socket?.emit('catch-up', {
          symbol,
          sinceTimestamp: adjustedTimestamp,
          lastMarkerTimestamp: this.lastMarkerTimestamp || undefined
        });

        // Safety net: if catch-up-data doesn't arrive within 3s, request historical data as fallback
        const catchUpTimeout = setTimeout(() => {
          const expectedCandles = Math.max(1, Math.floor(timeHiddenMs / (2 * 60 * 1000)) + 2);
          console.log(`⚠️ Catch-up timeout for ${symbol} - falling back to requestHistoricalData (expected ~${expectedCandles} candles)`);
          this.requestHistoricalData(symbol, Math.min(expectedCandles, 100));
        }, 3000);

        // Cancel timeout when catch-up-data arrives
        const onCatchUp = () => {
          clearTimeout(catchUpTimeout);
          this.socket?.off('catch-up-data', onCatchUp);
        };
        this.socket?.on('catch-up-data', onCatchUp);
      } else {
        // Fallback: request last 50 candles if we don't have state
        console.log(`📥 Fallback catch-up for ${symbol} (no prior state)`);
        this.requestHistoricalData(symbol, 50);
      }
    });
  }

  /**
   * Check broker connection status
   * With the relay architecture, broker state is automatically sent
   * via broker:list and broker:status events when main socket reconnects.
   * This method is now a no-op - kept for compatibility.
   */
  private restoreTradovateConnection(): void {
    // With the relay architecture, broker connections are managed by backend.
    // The backend maintains a persistent connection to tradovate-service.
    // When frontend reconnects to backend, it receives broker:list automatically.
    console.log('✅ Broker state managed by backend relay');
  }

  /**
   * Force reconnection to the WebSocket server
   * Used after long background periods where auto-reconnect may have failed
   */
  private forceReconnect(): void {
    // CRITICAL: Snapshot catch-up timestamps BEFORE reconnect.
    // Once the new socket connects, live market-data events will update
    // lastCandleTimestamp to the current time. The catch-up needs to use
    // the PRE-reconnect timestamp to fill the gap.
    this.catchUpSinceTimestamp = this.lastCandleTimestamp;

    console.log('🔄 Force reconnecting to WebSocket server...');
    console.log('🔄 [WS] forceReconnect state', {
      subscribedSymbols: Array.from(this.subscribedSymbols),
      pendingSubscriptions: Array.from(this.pendingSubscriptions),
      catchUpSinceTimestamp: this.catchUpSinceTimestamp,
      lastMarkerTimestamp: this.lastMarkerTimestamp,
    });

    // CRITICAL: Reset socket handlers flags BEFORE disconnecting old socket
    // This ensures handlers will be re-registered on the new socket
    resetBrokerSocketHandlers();
    resetSRSocketHandlers();
    resetAutoBeSocketHandlers();
    resetDisciplineSocketHandlers();
    resetRestrainSocketHandlers();
    resetAccountStatsSocketHandlers();
    resetToastSocketHandlers();
    resetVestVolumeSocketHandlers();
    resetVestPositionSocketHandlers();
    resetVestTpslSocketHandlers();
    resetVestSizingSocketHandlers();
    resetMesCandleFeedSocketHandlers();

    // Disconnect existing socket if any
    if (this.socket) {
      // Save subscribed symbols before disconnect
      const symbolsToResubscribe = Array.from(this.subscribedSymbols);

      try {
        this.socket.disconnect();
      } catch (e) {
        console.warn('Error disconnecting socket:', e);
      }
      this.socket = null;

      // Restore subscribed symbols (so they get resubscribed on connect)
      symbolsToResubscribe.forEach(s => this.subscribedSymbols.add(s));
    }

    // Create fresh socket connection
    console.log(`🔌 Creating new connection to ${this.serverUrl}...`);

    this.socket = io(this.serverUrl, {
      reconnection: true,
      reconnectionDelay: this.baseReconnectDelay,
      reconnectionDelayMax: this.maxReconnectDelay,
      reconnectionAttempts: this.maxReconnectAttempts,
      randomizationFactor: 0.25, // Adds jitter to reconnection delays
      timeout: 20000,
      transports: ['websocket'],
      forceNew: true, // Force new connection
      auth: {
        apiKey: import.meta.env.VITE_API_KEY || ''
      }
    });

    this.setupEventHandlers();

    // Re-initialize broker socket handlers on the new socket
    // Without this, broker:positions, broker:orders events won't be received!
    initBrokerSocketHandlers();
    initAccountStatsHandlers();
    initSRSocketHandlers();
    initAutoBeSocketHandlers();
    initDisciplineSocketHandlers(this);
    initRestrainSocketHandlers(this);
    initVestVolumeSocketHandlers();
    initVestPositionSocketHandlers();
    initVestTpslSocketHandlers();
    initVestSizingSocketHandlers();
    initMesCandleFeedSocketHandlers();

    // Note: Resubscription will happen in the 'connect' event handler
    // Catch-up will also be triggered there since it runs for each subscribed symbol
  }

  /**
   * Setup Socket.IO event handlers
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      log.info('Connected to server successfully');
      console.log(`🔌 [WS] Socket connected to ${this.serverUrl}! subscribedSymbols: ${Array.from(this.subscribedSymbols).join(',')}, pendingSubscriptions: ${Array.from(this.pendingSubscriptions).join(',')}`);
      const isReconnect = this.hasCompletedInitialConnect;
      this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
      this.lastPongTime = Date.now(); // Reset pong time on connect
      this.startHeartbeat(); // Restart heartbeat on reconnection
      this.startMarkerSync(); // Restart marker sync on reconnection
      useMarketStore.getState().setConnectionStatus('connected');

      // Emit connection state event for UI feedback
      this.emitConnectionState('connected');

      // Reconcile the shared Auto B/E setting: a toggle made on another
      // device while this socket was down arrived as a broadcast we missed.
      void fetchAutoBeState();

      // Same for restrain: ask for the live state (older backends just
      // never answer, and nothing is badged or gated).
      requestRestrainState();

      // Resubscribe to symbols after reconnection
      this.subscribedSymbols.forEach((symbol) => {
        this.subscribe(symbol, { skipHistorical: isReconnect });
      });

      // Process any pending subscriptions that were queued before connection
      if (this.pendingSubscriptions.size > 0) {
        log.info('Processing pending subscriptions', { count: this.pendingSubscriptions.size });
        this.pendingSubscriptions.forEach((symbol) => {
          this.subscribe(symbol);
        });
      }

      this.hasCompletedInitialConnect = true;

      // Restore Tradovate connection if it was previously connected
      this.restoreTradovateConnection();

      // Request fresh S/R levels now that socket is connected
      this.emit('get-sr-levels', { symbol: '' });
      console.log('📊 [S/R] Requested fresh levels after connect');

      // Fetch initial broker/trading state now that socket is connected
      const brokerStore = useBrokerStore.getState();
      brokerStore.fetchBrokers();
      brokerStore.fetchCopyTradingStatus();
      brokerStore.fetchRMSStatus();

      // Fetch discipline status
      useDisciplineStore.getState().fetchStatus();

      // Fetch toast history
      useToastStore.getState().fetchHistory();

      // Fetch trailing pullback markers
      this.fetchTrailingPullbackMarkers();

      // Fetch FVG zones for ICT strategy
      this.fetchFVGZones();

      // Fetch ICT settings to ensure chart renders zones on fresh load
      this.fetchICTSettings();

      // Load volume profile settings
      useVolumeProfileStore.getState().loadSettings();

      // Trigger catch-up if we were hidden and have valid timestamps
      // CRITICAL: Use catchUpSinceTimestamp (snapshotted at reconnect time), NOT lastCandleTimestamp
      // (which gets overwritten by live market-data events before catch-up fires)
      const marketStore = useMarketStore.getState();
      const catchUpTs = this.catchUpSinceTimestamp || this.lastCandleTimestamp;
      if (marketStore.dataMode === 'live' && catchUpTs > 0 && this.subscribedSymbols.size > 0) {
        console.log(`🔄 Triggering post-reconnect catch-up from ${new Date(catchUpTs).toLocaleTimeString()}...`);
        const CANDLE_OFFSET_MS = 2 * 2 * 60 * 1000;

        const sendCatchUp = (phase: string) => {
          this.subscribedSymbols.forEach((symbol) => {
            const adjustedTimestamp = catchUpTs - CANDLE_OFFSET_MS;
            console.log(`📥 ${phase} catch-up for ${symbol} since ${new Date(adjustedTimestamp).toLocaleTimeString()}`);
            this.socket?.emit('catch-up', {
              symbol,
              sinceTimestamp: adjustedTimestamp,
              lastMarkerTimestamp: this.lastMarkerTimestamp || undefined
            });
          });
        };

        // Phase 1: Send catch-up after 1s (gives subscribe time to process)
        setTimeout(() => sendCatchUp('Phase-1'), 1000);

        // Phase 2: Safety net - if candles still haven't updated after 5s,
        // request full historical data as fallback
        setTimeout(() => {
          const currentCandles = useMarketStore.getState().candles;
          const latestTs = currentCandles.length > 0 ? currentCandles[currentCandles.length - 1].timestamp : 0;
          const now = Date.now();
          const gapMs = now - latestTs;
          const expectedGap = 3 * 60 * 1000; // 3 minutes = more than 1 candle gap

          if (gapMs > expectedGap) {
            console.log(`⚠️ Phase-2 fallback: latest candle is ${Math.round(gapMs / 1000)}s old, requesting historical data`);
            this.subscribedSymbols.forEach((symbol) => {
              this.requestHistoricalData(symbol, 50);
            });
          } else {
            console.log(`✅ Phase-2 check: candles are fresh (${Math.round(gapMs / 1000)}s old)`);
          }
        }, 5000);
      }
    });

    this.socket.on('disconnect', (reason) => {
      log.warn('Disconnected from server', { reason });
      console.warn('🔌 [WS] Socket disconnected', {
        reason,
        subscribedSymbols: Array.from(this.subscribedSymbols),
        pendingSubscriptions: Array.from(this.pendingSubscriptions),
      });
      useMarketStore.getState().setConnectionStatus('disconnected');
      this.emitConnectionState('disconnected', reason);
    });

    this.socket.on('connect_error', (error) => {
      const delay = this.getReconnectDelay();
      log.error('Connection error', error, {
        attempt: this.reconnectAttempts + 1,
        maxAttempts: this.maxReconnectAttempts,
        nextRetryIn: `${Math.round(delay / 1000)}s`
      });
      this.reconnectAttempts++;
      useMarketStore.getState().setConnectionStatus('disconnected');
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      log.warn('Attempting to reconnect', {
        attempt: attemptNumber,
        maxAttempts: this.maxReconnectAttempts
      });
      this.emitConnectionState('reconnecting', undefined, attemptNumber);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      log.info('Reconnected to server', { attempts: attemptNumber });
      this.reconnectAttempts = 0; // Reset on successful reconnection
      useMarketStore.getState().setConnectionStatus('connected');
      this.emitConnectionState('connected');
    });

    this.socket.on('reconnect_error', (error) => {
      const delay = this.getReconnectDelay();
      log.error('Reconnection error', error, {
        attempt: this.reconnectAttempts,
        nextRetryIn: `${Math.round(delay / 1000)}s`
      });
    });

    this.socket.on('reconnect_failed', () => {
      log.error('Reconnection failed after max attempts', undefined, {
        maxAttempts: this.maxReconnectAttempts
      });
      useMarketStore.getState().setConnectionStatus('disconnected');
      this.emitConnectionState('failed');

      // Notify user to refresh the page
      console.error(
        '❌ Failed to reconnect to server after maximum attempts. Please refresh the page.'
      );
    });

    // Server status
    this.socket.on('server-status', (status: ServerStatus) => {
      log.debug('Server status received', {
        symbols: status.symbols,
        availableSymbols: status.availableSymbols,
        connections: status.connections,
        uptime: status.uptime
      });
      // `status.symbols` is the engine's CONFIGURED list -- the contracts it is
      // actually streaming. Feeding it back into the store keeps
      // `resolveActiveSymbol` correct for a tab that was open across a roll,
      // without waiting for a reload to re-hit /api/symbols.
      if (Array.isArray(status.symbols)) {
        useSymbolStore.getState().setActiveSymbols(status.symbols);
      }
    });

    // Historical data
    this.socket.on('historical-data', ({ symbol, candles }: HistoricalDataResponse) => {
      log.info('Received historical data', { symbol, count: candles.length });
      const marketStore = useMarketStore.getState();
      // Filter: only process historical data for the currently active symbol
      if (marketStore.symbol && symbol !== marketStore.symbol) {
        log.info('Ignoring historical data for inactive symbol', { received: symbol, active: marketStore.symbol });
        return;
      }

      // Clear analysis caches when symbol changes (different symbol = different data)
      const previousSymbol = marketStore.symbol;
      if (previousSymbol && previousSymbol !== symbol) {
        log.info('Symbol changed, clearing analysis caches', { from: previousSymbol, to: symbol });
        marketStore.clearPinningVolumeAnalysisCache();
        marketStore.clearWholeCandleAnalysisCache();
      }

      // Determine if this is a lazy load (prepend older data) or fresh load (replace)
      const existingCandles = marketStore.candles;
      const isLazyLoad = existingCandles.length > 0 &&
        candles.length > 0 &&
        candles[candles.length - 1].timestamp < existingCandles[0].timestamp;

      if (isLazyLoad) {
        // Lazy load: prepend older candles to existing data
        log.info('Lazy load detected - prepending older candles', {
          newCount: candles.length,
          existingCount: existingCandles.length,
          newestNew: new Date(candles[candles.length - 1].timestamp).toISOString(),
          oldestExisting: new Date(existingCandles[0].timestamp).toISOString()
        });
        marketStore.prependCandles(candles);
        marketStore.setLoadingOlderData(false);

        // If we got fewer candles than a day's worth (720), we've reached the start of available data
        if (candles.length < 720) {
          log.info('Fewer candles returned than requested - no more older data available');
          marketStore.setHasMoreOlderData(false);
        }

      } else {
        // Fresh load: replace all candles
        marketStore.setCandles(candles);
        marketStore.setSymbol(symbol);
        marketStore.setLoadingOlderData(false);
        marketStore.setHasMoreOlderData(true); // Reset for new data

        // Set current price from latest candle
        if (candles.length > 0) {
          const latestCandle = candles[candles.length - 1];
          marketStore.setCurrentPrice(latestCandle.close);
        }

      }
    });

    // Real-time market data
    this.socket.on('market-data', ({ symbol, candle, isNewCandle, trailing, ema9, ema21, ema200, vwap }: MarketDataUpdate) => {
      const marketStore = useMarketStore.getState();
      // IGNORE live data when in backtest mode
      if (marketStore.dataMode === 'backtest') {
        console.log(`⏸️ [WS IGNORE] Ignoring live data in backtest mode`);
        return;
      }


      // Attach trailing data and EMA values to candle if present (backend sends them when available)
      const candleWithIndicators = {
        ...candle,
        ...(trailing && { trailing }),
        ...(ema9 !== undefined && { ema9 }),
        ...(ema21 !== undefined && { ema21 }),
        ...(ema200 !== undefined && { ema200 }),
        ...(vwap !== undefined && { vwap }),
      };

      // Route to marketStore
      if (marketStore.symbol === symbol) {
        marketStore.setCurrentPrice(candle.close);

        // FIX: Invalidate analysis caches when candle OHLC data changes
        // Without this, hovering over a candle returns stale cached analysis
        // Use symbol:timestamp composite key
        const normalizedSymbol = normalizeSymbol(symbol);
        const cacheKey = `${normalizedSymbol}:${candle.timestamp}`;
        if (marketStore.wholeCandleAnalysisCache.has(cacheKey)) {
          marketStore.wholeCandleAnalysisCache.delete(cacheKey);
        }
        if (marketStore.pinningVolumeAnalysisCache.has(cacheKey)) {
          marketStore.pinningVolumeAnalysisCache.delete(cacheKey);
        }

        // Always update/add the candle for real-time updates (with trailing if present)
        marketStore.updateOrAddCandle(candleWithIndicators);

        // Track latest candle timestamp continuously for catch-up recovery
        // This ensures catch-up works even if visibilitychange doesn't fire (common on mobile)
        if (candle.timestamp > this.lastCandleTimestamp) {
          this.lastCandleTimestamp = candle.timestamp;
        }

        if (isNewCandle) {
          console.log(`📊 ${symbol} | New candle: ${candle.close.toFixed(2)}`);
        }
      }

    });

    // Error handling
    this.socket.on('error', ({ message, symbol }) => {
      log.error('Server error', undefined, { symbol, message });
    });

    // Ping/pong for connectivity check
    this.socket.on('pong', ({ timestamp }) => {
      const now = Date.now();
      this.lastPongTime = now; // Update last pong time for heartbeat
      const latency = now - timestamp;
      // Only log if latency is notable (>100ms)
      if (latency > 100) {
        console.log(`🏓 Pong received (latency: ${latency}ms)`);
      }
    });

    // Signal prediction events - store ALL predictions by symbol
    this.socket.on('signal-prediction', (prediction: SignalPrediction & { symbol?: string }) => {
      // Store prediction with symbol info - the store will handle per-symbol storage
      useSignalPredictorStore.getState().setPrediction(prediction);
    });

    // Session high/low updates for fib levels - store per-symbol
    // Updates both strategyService (for HOD/LOD values) and marketStore (for React re-renders)
    this.socket.on('session-high-low', ({ symbol, high, low }: { symbol?: string; high: number; low: number }) => {
      if (symbol) {
        // Store session data for this symbol (per-symbol storage)
        import('./StrategyService').then(({ strategyService }) => {
          strategyService.updateSessionHighLow(symbol, high, low);
        });
        // Also update marketStore to trigger re-renders in ChartView
        // This fixes the race condition where HOD/LOD effects run before data arrives
        useMarketStore.getState().setSessionHighLow(symbol, high, low);
      }
    });

    // Price levels updates (RTH HOD/LOD, PDH/PDL, IB, Midnight Open, Weekly/Monthly)
    this.socket.on('price-levels', (data: {
      symbol: string;
      sessionHigh: number;
      sessionLow: number;
      rthHigh: number;
      rthLow: number;
      prevDayHigh: number;
      prevDayLow: number;
      ibHigh: number;
      ibLow: number;
      midnightOpen: number;
      ibComplete: boolean;
      prevWeekHigh: number;
      prevWeekLow: number;
      currWeekHigh: number;
      currWeekLow: number;
      prevMonthHigh: number;
      prevMonthLow: number;
    }) => {
      if (data.symbol) {
        useMarketStore.getState().setPriceLevels(data.symbol, {
          sessionHigh: data.sessionHigh,
          sessionLow: data.sessionLow,
          rthHigh: data.rthHigh,
          rthLow: data.rthLow,
          prevDayHigh: data.prevDayHigh,
          prevDayLow: data.prevDayLow,
          ibHigh: data.ibHigh,
          ibLow: data.ibLow,
          midnightOpen: data.midnightOpen,
          ibComplete: data.ibComplete,
          prevWeekHigh: data.prevWeekHigh ?? 0,
          prevWeekLow: data.prevWeekLow ?? 0,
          currWeekHigh: data.currWeekHigh ?? 0,
          currWeekLow: data.currWeekLow ?? 0,
          prevMonthHigh: data.prevMonthHigh ?? 0,
          prevMonthLow: data.prevMonthLow ?? 0,
        });
      }
    });

    // Signal markers bulk update - replaces whole-candle markers (used after settings change)
    // Preserves existing volume markers (pinning volume strategy)
    // Store ALL markers - each component filters by its own symbol for split view support
    this.socket.on('signal-markers', (data: SignalMarker[] | { markers: SignalMarker[]; version: number }) => {
      const marketStore = useMarketStore.getState();

      // Handle both old format (array) and new format (object with markers and version)
      const markers = Array.isArray(data) ? data : data.markers;
      const version = Array.isArray(data) ? undefined : data.version;

      // Preserve existing volume markers (pinning volume strategy) - all symbols
      const existingVolumeMarkers = marketStore.signalMarkers.filter(
        m => m.type === 'volume_spike_bullish' || m.type === 'volume_spike_bearish'
      );
      // Preserve existing trailing pullback markers - all symbols
      const existingTrailingPullbackMarkers = marketStore.signalMarkers.filter(
        m => m.type === 'trailing_pullback_bullish' || m.type === 'trailing_pullback_bearish'
      );
      // Merge new whole-candle markers with existing volume and trailing pullback markers
      marketStore.setSignalMarkers([...markers, ...existingVolumeMarkers, ...existingTrailingPullbackMarkers]);
      if (version !== undefined) {
        marketStore.setMarkersVersion(version);
      }
      console.log(`📍 [MARKERS] Loaded ${markers.length} whole-candle markers, preserved ${existingVolumeMarkers.length} volume markers${version !== undefined ? `, version=${version}` : ''}`);
    });

    // Magnet candle strategy markers - batch on connect
    this.socket.on('magnet-markers-loaded', (markers: any) => {
      if (Array.isArray(markers)) {
        console.log(`[WS] Received ${markers.length} magnet markers (batch)`);
        useMarketStore.getState().setMagnetMarkers(markers);
      }
    });

    // Magnet candle strategy - single marker on new candle
    this.socket.on('magnet-marker', (marker: any) => {
      if (marker && marker.timestamp) {
        console.log(`[WS] Received magnet marker: ${marker.direction} at ${marker.price} for ${marker.symbol}`);
        useMarketStore.getState().addMagnetMarker(marker);
      }
    });

    // Signal marker events - for real-time marker updates
    // Store ALL markers - each component filters by its own symbol for split view support
    this.socket.on('signal-marker', (marker: SignalMarker) => {
      const marketStore = useMarketStore.getState();

      // Only process live markers when in live mode
      if (marketStore.dataMode === 'backtest') {
        return;
      }

      // Check if marker already exists (by timestamp and symbol) to avoid duplicates
      const existingMarkers = marketStore.signalMarkers;
      const alreadyExists = existingMarkers.some(m =>
        m.timestamp === marker.timestamp &&
        normalizeSymbol(m.symbol) === normalizeSymbol(marker.symbol)
      );

      if (!alreadyExists) {
        marketStore.addSignalMarker(marker);
      }
    });

    // Real-time profitability update for individual markers
    this.socket.on('marker-profitability-update', (update: { timestamp: number; symbol: string; isProfitable: boolean; pnl: number }) => {
      const marketStore = useMarketStore.getState();
      const markers = marketStore.signalMarkers;

      // Find and update the marker with profitability data
      const updatedMarkers = markers.map(m => {
        if (m.timestamp === update.timestamp && normalizeSymbol(m.symbol) === normalizeSymbol(update.symbol)) {
          return { ...m, isProfitable: update.isProfitable, pnl: update.pnl };
        }
        return m;
      });

      marketStore.setSignalMarkers(updatedMarkers);
      console.log(`💰 [PROFITABILITY] Updated marker at ${update.timestamp}: profitable=${update.isProfitable}, pnl=${update.pnl}`);
    });

    // Helper to normalize Rust snake_case to JS camelCase for volume markers
    const normalizeVolumeMarker = (m: any): SignalMarker => ({
      timestamp: m.timestamp,
      type: m.marker_type || m.type,
      symbol: m.symbol,
      volumeRatio: m.volume_ratio ?? m.volumeRatio,
      candleLength: m.candle_length ?? m.candleLength,
      wickPercentage: m.wick_percentage ?? m.wickPercentage,
      skipped: m.skipped,
      skipReason: m.skip_reason ?? m.skipReason,
      atrValue: m.atr_value ?? m.atrValue,
      atrLabel: m.atr_label ?? m.atrLabel,
      // Confluence fields
      confluenceScore: m.confluence_score ?? m.confluenceScore,
      confluenceDetails: m.confluence_details ?? m.confluenceDetails,
      confluenceGroup: m.confluence_group ?? m.confluenceGroup,
    });

    // Volume markers bulk update (pinning volume strategy) - merge with existing signal markers
    // Store ALL markers - each component filters by its own symbol for split view support
    this.socket.on('volume-markers', (markers: any[]) => {
      const marketStore = useMarketStore.getState();

      // Convert volume markers to SignalMarker format (all symbols)
      const normalizedMarkers = markers.map(normalizeVolumeMarker);

      // Filter out existing volume spike markers and preserve non-volume markers
      const nonVolumeMarkers = marketStore.signalMarkers.filter(
        m => m.type !== 'volume_spike_bullish' && m.type !== 'volume_spike_bearish'
      );
      marketStore.setSignalMarkers([...nonVolumeMarkers, ...normalizedMarkers]);
      console.log(`📊 [VOLUME MARKERS] Loaded ${normalizedMarkers.length} pinning volume markers`);
    });

    // Volume marker events - for real-time volume spike updates
    // Store ALL markers - each component filters by its own symbol for split view support
    this.socket.on('volume-marker', (marker: any) => {
      const marketStore = useMarketStore.getState();

      // Only process live markers when in live mode
      if (marketStore.dataMode === 'backtest') {
        return;
      }

      // Normalize the marker (Rust sends snake_case, frontend expects camelCase)
      const normalizedMarker = normalizeVolumeMarker(marker);

      // Check if marker already exists (by timestamp and symbol) to avoid duplicates
      const existingMarkers = marketStore.signalMarkers;
      const alreadyExists = existingMarkers.some(m =>
        m.timestamp === normalizedMarker.timestamp &&
        normalizeSymbol(m.symbol) === normalizeSymbol(normalizedMarker.symbol)
      );

      if (!alreadyExists) {
        marketStore.addSignalMarker(normalizedMarker);
        console.log(`📊 [VOLUME MARKER] Added: ${normalizedMarker.type} for ${normalizedMarker.symbol} at ${new Date(normalizedMarker.timestamp).toLocaleTimeString()}`);
      }
    });

    // Trailing pullback marker events - real-time trailing pullback signals
    this.socket.on('trailing-pullback-marker', (marker: any) => {
      const marketStore = useMarketStore.getState();

      // Only process live markers when in live mode
      if (marketStore.dataMode === 'backtest') {
        return;
      }

      // Normalize the marker (Rust sends snake_case, frontend expects camelCase)
      const normalizedMarker = {
        timestamp: marker.timestamp,
        type: marker.marker_type || marker.type,  // "trailing_pullback_bullish" or "trailing_pullback_bearish"
        symbol: marker.symbol,
        confidence: marker.confidence,
        classification: marker.classification,
        trailingDirection: marker.trailing_direction ?? marker.trailingDirection,
        candleDirection: marker.candle_direction ?? marker.candleDirection,
        wccAligned: marker.wcc_aligned ?? marker.wccAligned,
        skipped: marker.skipped,
        skipReason: marker.skip_reason ?? marker.skipReason,
      };

      // Check if marker already exists (by timestamp, symbol, and type) to avoid duplicates
      const existingMarkers = marketStore.signalMarkers;
      const alreadyExists = existingMarkers.some(m =>
        m.timestamp === normalizedMarker.timestamp &&
        normalizeSymbol(m.symbol) === normalizeSymbol(normalizedMarker.symbol) &&
        m.type === normalizedMarker.type
      );

      if (!alreadyExists) {
        marketStore.addSignalMarker(normalizedMarker);
        console.log(`🎯 [TRAILING PULLBACK] Added: ${normalizedMarker.type} (${normalizedMarker.classification}) for ${normalizedMarker.symbol} at ${new Date(normalizedMarker.timestamp).toLocaleTimeString()}`);
      }
    });

    // Donchian channel breakout markers - real-time
    this.socket.on('donchian-marker', (marker: DonchianMarker) => {
      const marketStore = useMarketStore.getState();
      if (marketStore.dataMode === 'backtest') {
        return;
      }
      useDonchianStore.getState().addMarker(marker);
      console.log(`[DONCHIAN] ${marker.markerType} breakout for ${marker.symbol} at ${new Date(marker.timestamp).toLocaleTimeString()}`);
    });

    // Donchian channel markers bulk load (historical) - on client connect
    this.socket.on('donchian-markers-loaded', (markers: DonchianMarker[]) => {
      useDonchianStore.getState().setMarkers(markers);
      console.log(`[DONCHIAN] Loaded ${markers.length} historical markers`);
    });

    // Diamond scored by the ENGINE — the exact score and grade handed to the
    // auto-bot, and the ONLY score a bar can have. There is no local scorer to
    // defer to any more: the copy that used to live in
    // `utils/setupCandidates.ts` had drifted (53/moderate on a bar the bot
    // shorted at 63/strong) and is deleted.
    //
    // The whole payload is kept, breakdown included. The engine serialises its
    // entire SetupCandidate here, and the popup explains the score from THESE
    // components rather than re-deriving them — a re-derivation would be a
    // second implementation of the formula, free to disagree with the number it
    // claims to explain.
    //
    // Fires only on rule hits, live, with no replay. Everything older than the
    // current connection comes from `GET /api/diamonds/history`, which runs the
    // same `detect_candidates` and builds its objects through the same
    // `diamond_payload` helper this emit uses. The chart refetches on reconnect
    // for exactly that reason — bars that completed while the socket was down
    // produced diamonds nobody received.
    //
    // Sharing a serialiser is NOT by itself a guarantee that the two agree, and
    // this comment used to claim "byte-identical" on that basis while they did
    // not. `trendAlignment` is not a struct field — it is grafted onto the JSON
    // after serialisation — and only the endpoint was doing it, so a live
    // diamond showed `bars:` with no `emas:` until the page was refreshed. The
    // helper now owns that step for both paths. The endpoint still adds ONE
    // thing this emit cannot: `simulated`, a forward-looking outcome a bar that
    // has only just closed does not have yet.
    this.socket.on('diamond-signal', (d: EngineDiamond) => {
      const marketStore = useMarketStore.getState();
      if (marketStore.dataMode === 'backtest') {
        return;
      }
      // A missing symbol means an engine older than the symbol-tagged emit.
      // Dropping it is right: applying it to whichever chart is open would put
      // one symbol's grade on another's bar.
      if (!d?.symbol || typeof d.timestamp !== 'number') {
        console.warn('[DIAMOND] ignoring signal with no symbol/timestamp', d);
        return;
      }
      useCandidateStore.getState().recordEngineDiamond(d);
      console.log(
        `[DIAMOND] engine scored ${d.symbol} ${d.score.toFixed(0)} ${d.grade} at ${new Date(d.timestamp).toLocaleTimeString()}`
      );
    });

    // Break of Structure (BOS) markers - real-time
    this.socket.on('break-of-structure-marker', (marker: BosMarker) => {
      const marketStore = useMarketStore.getState();
      if (marketStore.dataMode === 'backtest') {
        return;
      }
      useBosStore.getState().addMarker(marker);
      console.log(`[BOS] ${marker.pending ? 'preliminary ' : ''}${marker.direction} break for ${marker.symbol} @ ${marker.breakLevel} at ${new Date(marker.timestamp).toLocaleTimeString()}`);
    });

    // Break of Structure (BOS) preliminary marker removal - a pending break
    // faked out (did not confirm on close). Remove it by its stable id.
    this.socket.on('break-of-structure-marker-remove', (payload: { id: string; symbol: string }) => {
      const marketStore = useMarketStore.getState();
      if (marketStore.dataMode === 'backtest') {
        return;
      }
      useBosStore.getState().removeMarker(payload.id);
      console.log(`[BOS] removed preliminary break ${payload.id} for ${payload.symbol}`);
    });

    // Break of Structure (BOS) markers bulk load (historical) - on client connect
    this.socket.on('bos-markers-loaded', (markers: BosMarker[]) => {
      useBosStore.getState().setMarkers(markers);
      console.log(`[BOS] Loaded ${markers.length} historical markers`);
    });

    // Pinning Volume analysis response - cache in store for overlay display
    // Store ALL responses with symbol-aware cache keys
    this.socket.on('pinning-volume-analysis', (response: { timestamp: number; symbol?: string; analysis: any }) => {
      if (response.analysis) {
        const marketStore = useMarketStore.getState();
        // Use response symbol or fall back to current symbol
        const responseSymbol = response.symbol || marketStore.symbol;
        marketStore.setPinningVolumeAnalysis(responseSymbol, response.timestamp, response.analysis);
      }
    });

    // Whole Candle analysis response - cache in store for overlay display
    // Store ALL responses with symbol-aware cache keys
    this.socket.on('whole-candle-analysis', (response: { timestamp: number; symbol?: string; analysis: any }) => {
      if (response.analysis) {
        const marketStore = useMarketStore.getState();
        // Use response symbol or fall back to current symbol
        const responseSymbol = response.symbol || marketStore.symbol;
        marketStore.setWholeCandleAnalysis(responseSymbol, response.timestamp, response.analysis);
      }
    });

    // FVG zones bulk update - replaces all FVG zones
    this.socket.on('fvg-zones', (zones: any[]) => {
      const marketStore = useMarketStore.getState();
      marketStore.setFVGZones(zones);
      console.log(`📦 [FVG] Loaded ${zones.length} FVG zones`);
    });

    // FVG zone single update - new zone detected or zone updated
    this.socket.on('fvg-zone', (zone: any) => {
      const marketStore = useMarketStore.getState();

      // Only process live zones when in live mode
      if (marketStore.dataMode === 'backtest') {
        return;
      }

      // Check if zone exists (update) or is new (add)
      const existingZones = marketStore.fvgZones;
      const existingIndex = existingZones.findIndex(z => z.id === zone.id);

      if (existingIndex >= 0) {
        marketStore.updateFVGZone(zone);
        console.log(`📦 [FVG] Updated: ${zone.zoneType} ${zone.timeframe} zone for ${zone.symbol}`);
      } else {
        marketStore.addFVGZone(zone);
        console.log(`📦 [FVG] Added: ${zone.zoneType} ${zone.timeframe} zone for ${zone.symbol}`);
      }
    });

    // Catch-up data handler - for visibility change recovery
    this.socket.on('catch-up-data', ({ symbol, candles, markers }: {
      symbol: string;
      candles: Candle[];
      markers: SignalMarker[];
      sinceTimestamp: number;
      serverTime: number;
    }) => {
      const marketStore = useMarketStore.getState();
      // Only process in live mode
      if (marketStore.dataMode !== 'live') {
        return;
      }

      // Filter: only process catch-up data for the currently active symbol
      if (symbol && symbol !== marketStore.symbol) {
        log.info('Ignoring catch-up data for inactive symbol', { received: symbol, active: marketStore.symbol });
        return;
      }

      // Validate symbol
      if (!symbol) {
        console.warn('⚠️ [CATCH-UP] No symbol in catch-up data, using marketStore symbol');
      }
      const catchUpSymbol = symbol || marketStore.symbol;

      // Merge candles into existing data (update existing or add new)
      if (candles.length > 0) {
        // Invalidate analysis caches for candles being updated (they may have new OHLC data)
        // Use symbol:timestamp composite key
        const normalizedSym = normalizeSymbol(catchUpSymbol);
        const wccCacheSize = marketStore.wholeCandleAnalysisCache.size;
        const pvCacheSize = marketStore.pinningVolumeAnalysisCache.size;

        if (wccCacheSize > 0 || pvCacheSize > 0) {
          candles.forEach(candle => {
            const cacheKey = `${normalizedSym}:${candle.timestamp}`;
            // Clear WCC analysis cache for this timestamp
            if (marketStore.wholeCandleAnalysisCache.has(cacheKey)) {
              marketStore.wholeCandleAnalysisCache.delete(cacheKey);
            }
            // Clear PV analysis cache for this timestamp
            if (marketStore.pinningVolumeAnalysisCache.has(cacheKey)) {
              marketStore.pinningVolumeAnalysisCache.delete(cacheKey);
            }
          });
          const wccDeleted = wccCacheSize - marketStore.wholeCandleAnalysisCache.size;
          const pvDeleted = pvCacheSize - marketStore.pinningVolumeAnalysisCache.size;
          if (wccDeleted > 0 || pvDeleted > 0) {
            console.log(`🔄 Invalidated ${wccDeleted} WCC + ${pvDeleted} PV cached analyses during catch-up`);
          }
        }

        // Batch merge catch-up candles into store in a single update
        // This prevents multiple renders that confuse TradingView's update() method
        const existingCandles = [...marketStore.candles];
        let newCount = 0;
        for (const candle of candles) {
          const idx = existingCandles.findIndex(c => c.timestamp === candle.timestamp);
          if (idx >= 0) {
            existingCandles[idx] = candle;
          } else {
            existingCandles.push(candle);
            newCount++;
          }
        }
        existingCandles.sort((a, b) => a.timestamp - b.timestamp);
        marketStore.setCandles(existingCandles);
        // Bump version to force chart to use setData() instead of update()
        marketStore.bumpCandlesVersion();

        // Update current price from latest candle
        const latestCandle = candles[candles.length - 1];
        marketStore.setCurrentPrice(latestCandle.close);

        // Track latest timestamp for future catch-ups
        if (latestCandle.timestamp > this.lastCandleTimestamp) {
          this.lastCandleTimestamp = latestCandle.timestamp;
        }


        console.log(`🔄 [CATCH-UP] Merged ${candles.length} candles (${newCount} new) for ${catchUpSymbol}`);
      }

      // Merge markers (avoid duplicates by timestamp and symbol)
      if (markers.length > 0) {
        const existingMarkers = marketStore.signalMarkers;
        markers.forEach(marker => {
          const alreadyExists = existingMarkers.some(m =>
            m.timestamp === marker.timestamp &&
            normalizeSymbol(m.symbol) === normalizeSymbol(marker.symbol)
          );
          if (!alreadyExists) {
            marketStore.addSignalMarker(marker);
          }
        });
      }
    });

    // Backtest tick data handler
    this.socket.on('backtest-tick-data', (response: any) => {
      // Validate tick data structure
      if (!response.ticks || !Array.isArray(response.ticks)) {
        console.error('❌ Invalid backtest response: ticks is not an array', response);
        return;
      }

      // Check if this is chunked data
      const isChunked = response.totalChunks !== undefined && response.totalChunks > 1;

      if (isChunked) {
        console.log(`📦 Received chunk ${response.chunkIndex + 1}/${response.totalChunks} (${response.ticks.length.toLocaleString()} ticks)`);

        // Initialize buffer on first chunk
        if (response.chunkIndex === 0) {
          this.backtestChunkBuffer = [];
          this.expectedTotalChunks = response.totalChunks;
        }

        // Add ticks to buffer (use concat to avoid stack overflow with large arrays)
        this.backtestChunkBuffer = this.backtestChunkBuffer.concat(response.ticks);

        // If not all chunks received yet, wait for more
        if (response.chunkIndex < response.totalChunks - 1) {
          console.log(`⏳ Waiting for ${response.totalChunks - response.chunkIndex - 1} more chunk(s)...`);
          return;
        }

        // All chunks received - use the accumulated buffer
        console.log(`✅ All ${response.totalChunks} chunks received. Total ticks: ${this.backtestChunkBuffer.length.toLocaleString()}`);
        response.ticks = this.backtestChunkBuffer;
        this.backtestChunkBuffer = [];
        this.expectedTotalChunks = 0;
      }

      console.log(`📥 Processing ${response.totalTicks?.toLocaleString() ?? 'unknown'} backtest ticks for ${response.symbol}`);
      console.log(`📊 Date range: ${response.startDate ? new Date(response.startDate).toLocaleString() : 'unknown'} - ${response.endDate ? new Date(response.endDate).toLocaleString() : 'unknown'}`);

      // Log first few ticks
      if (response.ticks.length > 0) {
        console.log(`🔍 First 3 ticks:`, response.ticks.slice(0, 3).map((t: any) => ({
          timestamp: t.timestamp,
          timestampType: typeof t.timestamp,
          price: t.price,
          volume: t.volume,
          isDate: t.timestamp instanceof Date
        })));
      }

      const marketStore = useMarketStore.getState();

      try {
        console.log(`🎬 Loading ${response.ticks.length} ticks into BacktestPlayer...`);

        // Load ticks into backtest player for tick-by-tick playback
        const player = getBacktestPlayer();
        player.loadTicks(response.ticks, 2); // 2-minute bars

        console.log(`✅ Ticks loaded successfully, updating market store...`);

        marketStore.setSymbol(response.symbol);
        marketStore.setBacktestLoading(false);
        marketStore.setDataMode('backtest');

        // Don't clear candles here - the BacktestPlayer will manage candle state
        // Clearing here would destroy candles that were built during seek/restoration
        // marketStore.setCandles([]);

        // If there's a pending seek timestamp, seek to it now (with a small delay to ensure candles are set)
        if (this.pendingSeekTimestamp) {
          const seekTimestamp = this.pendingSeekTimestamp;
          console.log(`🎯 Seeking to timestamp:`, new Date(seekTimestamp).toISOString());

          // Use setTimeout to ensure the seek happens after the initial state is set
          setTimeout(async () => {
            console.log(`🎯 Executing seek to timestamp:`, new Date(seekTimestamp).toISOString());
            await player.seekToTimestamp(seekTimestamp);
          }, 100);

          this.pendingSeekTimestamp = undefined; // Clear it after scheduling
        }

        console.log(`✅ Backtest mode activated`);
      } catch (error) {
        console.error('❌ Error setting up backtest:', error);
        marketStore.setBacktestLoading(false);
      }
    });

    // =========================================================================
    // =========================================================================
    // RMS (Risk Management System) events - global, not broker-specific
    // =========================================================================

    this.socket.on('rms:order-rejected', (data: {
      type: string;
      reason: string;
    }) => {
      console.warn('[RMS] Order rejected:', data.reason);
      useToastStore.getState().addToast({
        type: 'error',
        message: `Order rejected: ${data.reason}`,
        duration: 4000,
      });
    });

    this.socket.on('rms:error', (data: { message: string }) => {
      console.error('[RMS] Error:', data.message);
      useToastStore.getState().addToast({
        type: 'error',
        message: `RMS Error: ${data.message}`,
        duration: 4000,
      });
    });

    // =========================================================================
    // Multi-Broker events
    // =========================================================================

    this.socket.on('broker:list', (data: { brokers: BrokerState[] }) => {
      useBrokerStore.getState().setBrokers(data.brokers);
    });

    this.socket.on('broker:connected', (_data: { broker: BrokerState }) => {
      // Handled by promise-based connectBroker action
    });

    this.socket.on('broker:disconnected', (_data: { brokerId: string }) => {
      // Handled by promise-based disconnectBroker action
    });

    this.socket.on('broker:status', (data: {
      brokerId: string;
      status: BrokerState['status'];
      error?: string;
    }) => {
      if (isHiddenBrokerId(data.brokerId)) return;
      useBrokerStore.getState().updateBrokerStatus(data.brokerId, data.status, data.error);

      // Toast notifications for broker status changes
      const brokerName = useBrokerStore.getState().getBroker(data.brokerId)?.name || data.brokerId;
      if (data.status === 'connected') {
        useToastStore.getState().addToast({ type: 'success', message: `${brokerName} connected` });
      } else if (data.status === 'error' && data.error) {
        useToastStore.getState().addToast({ type: 'error', message: `${brokerName}: ${data.error}`, duration: 5000 });
      } else if (data.status === 'disconnected') {
        useToastStore.getState().addToast({ type: 'info', message: `${brokerName} disconnected` });
      }
    });

    this.socket.on('broker:accounts', (data: { brokerId: string; accounts: BrokerAccount[] }) => {
      useBrokerStore.getState().setBrokerAccounts(data.brokerId, data.accounts);
    });

    this.socket.on('broker:account-balances', (data: { brokerId: string; accountBalances: Record<number, any> }) => {
      useBrokerStore.getState().updateBrokerAccountBalances(data.brokerId, data.accountBalances);
    });

    this.socket.on('broker:credentials-saved', () => {
      // Handled by promise-based saveBrokerCredentials action
    });

    this.socket.on('broker:error', (_data: { brokerId: string; error: string }) => {
      // Handled by promise-based actions
    });

    // =========================================================================
    // Copy Trading events
    // =========================================================================

    this.socket.on('copytrade:status', (data: {
      enabled: boolean;
      config: CopyTradingConfig | null;
      isListening: boolean;
      historyCount: number;
    }) => {
      useBrokerStore.getState().setCopyTradingEnabled(data.enabled);
      useBrokerStore.getState().setCopyTradingConfig(data.config);
    });

    this.socket.on('copytrade:config', (data: { config: CopyTradingConfig }) => {
      useBrokerStore.getState().setCopyTradingConfig(data.config);
    });

    this.socket.on('copytrade:config-updated', () => {
      // Handled by promise-based updateCopyTradingConfig action
    });

    this.socket.on('copytrade:enabled', () => {
      useBrokerStore.getState().setCopyTradingEnabled(true);
    });

    this.socket.on('copytrade:disabled', () => {
      useBrokerStore.getState().setCopyTradingEnabled(false);
    });

    this.socket.on('copytrade:executed', (data: CopyTradeEvent) => {
      useBrokerStore.getState().addCopyTradeEvent(data);
      useToastStore.getState().addToast({
        type: 'info',
        message: `Copy trade: ${data.type} ${data.followerResults.length} followers`,
        duration: 3000,
      });
    });

    this.socket.on('copytrade:history', (data: { history: CopyTradeEvent[] }) => {
      useBrokerStore.getState().setCopyTradingHistory(data.history);
    });

    this.socket.on('copytrade:error', (data: { error: string }) => {
      console.error('[WS] Copy trade error:', data);
      useToastStore.getState().addToast({
        type: 'error',
        message: `Copy trading error: ${data.error}`,
        duration: 4000,
      });
    });

    this.socket.on('copytrade:activity', (data: CopyTradeActivity) => {
      useBrokerStore.getState().addCopyTradeActivity(data);

      // Show toast for important events
      const importantEvents = ['order:copied', 'order:failed', 'flatten:copied', 'flatten:failed', 'error:general', 'error:connection', 'error:order', 'reconcile:mismatch'];
      if (importantEvents.includes(data.event)) {
        const isError = data.level === 'error' || data.event.includes('failed') || data.event.includes('mismatch');
        useToastStore.getState().addToast({
          type: isError ? 'error' : 'success',
          message: `Copy Trade ${data.event.replace(':', ' ')}: ${data.msg}`,
          duration: isError ? 8000 : 4000,
        });
      }
    });

    // =========================================================================
    // Market Hours events (from cme-market-hours microservice via backend relay)
    // =========================================================================

    this.socket.on('market:status', (data: any) => {
      useMarketStore.getState().setMarketHoursStatus(data);
    });

    this.socket.on('market:open', (data: any) => {
      console.log('[MARKET-HOURS] Market opened');
      useMarketStore.getState().setMarketHoursStatus(data);
      useToastStore.getState().addToast({
        type: 'success',
        message: 'Market is now open',
        duration: 5000,
      });
    });

    this.socket.on('market:close', (data: any) => {
      console.log('[MARKET-HOURS] Market closed');
      useMarketStore.getState().setMarketHoursStatus(data);
      useToastStore.getState().addToast({
        type: 'warning',
        message: 'Market is now closed',
        duration: 5000,
      });
    });

    this.socket.on('market:maintenance', (data: any) => {
      console.log('[MARKET-HOURS] Maintenance started');
      useMarketStore.getState().setMarketHoursStatus(data);
      useToastStore.getState().addToast({
        type: 'warning',
        message: 'Daily maintenance break started',
        duration: 5000,
      });
    });

    this.socket.on('market:resume', (data: any) => {
      console.log('[MARKET-HOURS] Maintenance ended');
      useMarketStore.getState().setMarketHoursStatus(data);
      useToastStore.getState().addToast({
        type: 'success',
        message: 'Market resumed after maintenance',
        duration: 5000,
      });
    });

    this.socket.on('session:change', (data: any) => {
      console.log(`[MARKET-HOURS] Session changed: ${data.previousSession} -> ${data.currentSession}`);
      useMarketStore.getState().setSessionInfo({
        previousSession: data.previousSession,
        currentSession: data.currentSession,
        isRTH: data.rthActive,
        isETH: data.ethActive,
      });
    });

    this.socket.on('session:rth:open', () => {
      console.log('[MARKET-HOURS] RTH opened');
      useMarketStore.getState().setRTHActive(true);
      useToastStore.getState().addToast({
        type: 'info',
        message: 'Regular Trading Hours opened (8:30 AM CT)',
        duration: 5000,
      });
    });

    this.socket.on('session:rth:close', () => {
      console.log('[MARKET-HOURS] RTH closed');
      useMarketStore.getState().setRTHActive(false);
      useToastStore.getState().addToast({
        type: 'info',
        message: 'Regular Trading Hours closed (3:00 PM CT)',
        duration: 5000,
      });
    });

    this.socket.on('holiday:early-close', (data: any) => {
      console.log(`[MARKET-HOURS] Holiday early close: ${data.holiday?.name}`);
      useMarketStore.getState().setUpcomingHoliday(data.holiday);
      useToastStore.getState().addToast({
        type: 'warning',
        message: `Early close today: ${data.holiday?.name} at ${data.holiday?.closeTime}`,
        duration: 10000,
      });
    });

    this.socket.on('holiday:closed', (data: any) => {
      console.log(`[MARKET-HOURS] Holiday closed: ${data.holiday?.name}`);
      useMarketStore.getState().setUpcomingHoliday(data.holiday);
      useToastStore.getState().addToast({
        type: 'warning',
        message: `Market closed for ${data.holiday?.name}`,
        duration: 10000,
      });
    });

    this.socket.on('holiday:warning', (data: any) => {
      console.log(`[MARKET-HOURS] Holiday warning: ${data.holiday?.name}`);
      useMarketStore.getState().setUpcomingHoliday(data.holiday);
      useToastStore.getState().addToast({
        type: 'info',
        message: `Upcoming holiday: ${data.holiday?.name} on ${data.holiday?.date}`,
        duration: 8000,
      });
    });

    // ========================================================================
    // HEALTH CHECK ALERTS
    // ========================================================================

    this.socket.on('health:alert', (data: {
      type: string;
      message: string;
      staleSymbols: Array<{ symbol: string; staleSecs: number }>;
      healthySymbols: string[];
      timestamp: number;
    }) => {
      console.warn(`[HEALTH] Alert: ${data.message}`);

      // Show toast with stale symbol details
      const staleList = data.staleSymbols?.map(s => `${s.symbol} (${Math.round(s.staleSecs / 60)}min)`).join(', ');
      useToastStore.getState().addToast({
        type: 'warning',
        message: `⚠️ Data streaming stopped: ${staleList}`,
        duration: 15000, // Show for 15 seconds
      });
    });

    // ========================================================================
    // PRICE & VOLUME ALERTS
    // ========================================================================

    // Import alert store dynamically to avoid circular deps
    const getAlertStore = () => import('../store/alertStore').then(m => m.useAlertStore.getState());

    this.socket.on('price-alert', async (data: {
      alertId: string;
      symbol: string;
      price: number;
      direction: string;
      currentPrice: number;
      triggerMode: string;
    }) => {
      console.log(`[ALERT] Price alert triggered: ${data.symbol} ${data.direction} ${data.price}`);
      useToastStore.getState().addToast({
        type: 'warning',
        message: `🔔 ${data.symbol}: Price ${data.direction === 'above' ? 'above' : 'below'} ${data.price.toFixed(2)}`,
        duration: 8000,
      });
    });

    this.socket.on('volume-alert', async (data: {
      alertId: string;
      symbol: string;
      volume: number;
      multiplier: number;
      avgVolume: number;
    }) => {
      console.log(`[ALERT] Volume alert triggered: ${data.symbol} ${data.volume} (${data.multiplier.toFixed(1)}x avg)`);
      useToastStore.getState().addToast({
        type: 'warning',
        message: `🔊 ${data.symbol}: Volume spike ${data.multiplier.toFixed(1)}x average (${data.volume.toLocaleString()})`,
        duration: 8000,
      });
    });

    this.socket.on('alert:created', async (alert: any) => {
      console.log(`[ALERT] Alert created: ${alert.id}`);
      const store = await getAlertStore();
      store.addAlert(alert);
    });

    this.socket.on('alert:updated', async (data: { id: string; [key: string]: any }) => {
      console.log(`[ALERT] Alert updated: ${data.id}`);
      const store = await getAlertStore();
      store.updateAlertFromSocket(data.id, data);
    });

    this.socket.on('alert:deleted', async (data: { id: string }) => {
      console.log(`[ALERT] Alert deleted: ${data.id}`);
      const store = await getAlertStore();
      store.removeAlert(data.id);
    });

    // ========================================================================
    // VOLUME PROFILE & ORDER FLOW EVENTS
    // ========================================================================

    this.socket.on('footprint-update', (data: { symbol: string; footprint: FootprintData }) => {
      useVolumeProfileStore.getState().addFootprint(data.symbol, data.footprint);
    });

    this.socket.on('delta-update', (data: {
      symbol: string;
      timestamp: number;
      delta: number;
      cumulativeDelta: number;
    }) => {
      useVolumeProfileStore.getState().addDeltaPoint(data.symbol, {
        timestamp: data.timestamp,
        delta: data.delta,
        cumulativeDelta: data.cumulativeDelta,
      });
    });

    this.socket.on('volume-profile-snapshot', (data: {
      symbol: string;
      snapshot: VolumeProfileSnapshot;
    }) => {
      useVolumeProfileStore.getState().setProfileSnapshot(data.symbol, data.snapshot);
    });

    // Session + weekly POC/VAH/VAL. The engine sends this only when a level
    // actually moves (checked every 5s), so it is cheap to keep current.
    this.socket.on('volume-profile-levels', (data: { symbol: string; levels: ProfileLevels | null }) => {
      if (data?.symbol) useProfileLevelsStore.getState().setLevels(data.symbol, data.levels ?? null);
    });

    // ========================================================================
    // NEWS & CALENDAR EVENTS
    // ========================================================================

    this.socket.on('news:status', (data: { status: 'disconnected' | 'connecting' | 'connected' }) => {
      console.log(`[NEWS] Connection status: ${data.status}`);
      useNewsStore.getState().setConnectionStatus(data.status);
    });

    this.socket.on('news:init', (data: { headlines: NewsHeadline[]; calendarEvents: CalendarEvent[] }) => {
      console.log(`[NEWS] Initialized with ${data.headlines?.length || 0} headlines, ${data.calendarEvents?.length || 0} calendar events`);
      useNewsStore.getState().initFromBackend(data);
    });

    this.socket.on('news:headline', (headline: NewsHeadline) => {
      useNewsStore.getState().addHeadline(headline);
    });

    this.socket.on('news:calendar', (event: CalendarEvent) => {
      useNewsStore.getState().updateCalendarEvent(event);
    });

    // S/R proximity alerts - show toast and forward as position-alert for Android widget
    this.socket.on('sr-proximity-alert', (data: any) => {
      const icon = data.alertType === 'sr_breached' ? '🔴' : '🟡';
      const msg = `${icon} ${data.symbol}: ${data.direction} @ ${data.price?.toFixed(2)} (level ${data.levelPrice?.toFixed(2)}, ${data.timeframe})`;
      useToastStore.getState().addToast({ type: data.alertType === 'sr_breached' ? 'warning' : 'info', message: msg, duration: 10000 });
    });
  }

  /**
   * Subscribe to symbol updates
   */
  subscribe(symbol: string, options: SubscribeOptions = {}): void {
    if (!this.socket?.connected) {
      log.info('Queuing subscription for when connected', { symbol });
      console.log('📡 [WS] queue subscribe', { symbol, options });
      this.pendingSubscriptions.add(symbol);
      return;
    }

    log.info('Subscribing to symbol', { symbol, skipHistorical: options.skipHistorical === true });
    console.log('📡 [WS] subscribe emit', {
      symbol,
      skipHistorical: options.skipHistorical === true,
      subscribedSymbols: Array.from(this.subscribedSymbols),
    });
    this.socket.emit('subscribe', {
      symbol,
      action: 'subscribe',
      ...(options.skipHistorical ? { skipHistorical: true } : {}),
      ...(options.allowExpired ? { allowExpired: true } : {}),
    });
    this.subscribedSymbols.add(symbol);
    this.pendingSubscriptions.delete(symbol); // Remove from pending if it was there
  }

  /**
   * Unsubscribe from symbol updates
   */
  unsubscribe(symbol: string): void {
    // Always update local state, even if disconnected
    this.subscribedSymbols.delete(symbol);
    this.pendingSubscriptions.delete(symbol);
    console.log('📡 [WS] unsubscribe', {
      symbol,
      connected: this.socket?.connected || false,
      subscribedSymbols: Array.from(this.subscribedSymbols),
      pendingSubscriptions: Array.from(this.pendingSubscriptions),
    });

    if (!this.socket?.connected) {
      console.log(`📤 Unsubscribing from ${symbol} (offline - local state updated)`);
      return;
    }

    console.log(`📤 Unsubscribing from ${symbol}...`);
    this.socket.emit('subscribe', { symbol, action: 'unsubscribe' });
  }

  /**
   * Request historical data for a symbol
   */
  requestHistoricalData(symbol: string, count: number = 200): void {
    if (!this.socket?.connected) {
      console.warn('Cannot request data - not connected to server');
      return;
    }

    console.log(`📥 Requesting ${count} bars for ${symbol}...`);
    this.socket.emit('get-historical', { symbol, count });
  }

  /**
   * Request historical data BEFORE a specific timestamp (for lazy loading older data)
   * @param symbol - The symbol to request data for
   * @param before - Timestamp in milliseconds - get candles before this time
   * @param count - Number of candles to request (default: 720 = 1 day)
   */
  requestHistoricalDataBefore(symbol: string, before: number, count: number = 720): void {
    if (!this.socket?.connected) {
      console.warn('Cannot request data - not connected to server');
      return;
    }

    console.log(`📥 Requesting ${count} bars for ${symbol} before ${new Date(before).toISOString()}...`);
    this.socket.emit('get-historical', { symbol, count, before });
  }

  /**
   * Send ping to check connectivity
   */
  ping(): void {
    if (!this.socket?.connected) {
      return;
    }

    this.socket.emit('ping');
  }

  /**
   * Disconnect from server
   */
  disconnect(): void {
    // Stop heartbeat
    this.stopHeartbeat();

    // Remove visibility handlers
    if (this.visibilityHandlerBound && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandlerBound);
      this.visibilityHandlerBound = null;
    }
    if (this.pageResumeHandlerBound && typeof window !== 'undefined') {
      window.removeEventListener('focus', this.pageResumeHandlerBound);
      window.removeEventListener('pageshow', this.pageResumeHandlerBound);
      this.pageResumeHandlerBound = null;
      log.debug('Visibility change handlers removed');
    }

    // Remove online handler
    if (this.onlineHandlerBound && typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineHandlerBound);
      this.onlineHandlerBound = null;
      log.debug('Online handler removed');
    }

    if (this.socket) {
      log.info('Disconnecting from server');
      this.socket.disconnect();
      this.socket = null;
      this.subscribedSymbols.clear();
      useMarketStore.getState().setConnectionStatus('disconnected');
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get subscribed symbols
   */
  getSubscribedSymbols(): string[] {
    return Array.from(this.subscribedSymbols);
  }

  /**
   * Request backtest data for a symbol and date range
   */
  requestBacktestData(symbol: string, startDate: Date, endDate: Date): void {
    if (!this.socket?.connected) {
      console.warn('Cannot request backtest data - not connected to server');
      return;
    }

    console.log(`📥 Requesting backtest data for ${symbol}...`);
    console.log(`📊 Date range: ${startDate.toLocaleString()} - ${endDate.toLocaleString()}`);

    // Convert dates to Unix timestamps (milliseconds)
    const request = {
      symbol,
      startDate: startDate.getTime(),
      endDate: endDate.getTime(),
      barInterval: 2, // 2-minute bars
    };

    this.socket.emit('backtest-request', request);
  }

  /**
   * Switch to live mode
   */
  switchToLiveMode(symbol: string): void {
    console.log(`📡 Switching to live mode for ${symbol}...`);

    // Update store - clear backtest state first
    const marketStore = useMarketStore.getState();
    marketStore.setBacktestLoading(false);
    marketStore.setBacktestConfig(null);
    marketStore.setDataMode('live');
    marketStore.setCandles([]); // Clear old candles

    // Subscribe to live data and request last 3 days of historical data
    // 3 days at 2-min intervals = ~2160 bars
    this.subscribe(symbol);
    this.requestHistoricalData(symbol, 2160);
  }

  /**
   * Generic emit method for sending events to the server
   */
  emit(event: string, data?: any): void {
    if (!this.socket?.connected) {
      console.warn(`⚠️ Cannot emit ${event}: socket not connected. Server URL: ${this.serverUrl}`);
      return;
    }

    // Stamp anything that can move a position with WHO asked for it.
    //
    // This is the single chokepoint every order path goes through, which is
    // the point: on 2026-08-25 seven market buys left this app in three
    // seconds and afterwards there was no way to tell which control emitted
    // them. Stamping at the individual call sites would have missed whichever
    // one it actually was. See #391.
    if (POSITION_MOVING_EVENTS.has(event)) {
      const origin = captureOrderOrigin(event);
      data = { ...(data ?? {}), origin };
      // console.error, not log: this must survive log filtering and stand out
      // in a recorded session. An order leaving the app is not routine.
      console.error(`🔴 [ORDER-PROV] emitting ${event}`, { origin, payload: data });
    }

    // Skip logging for noisy/frequent events
    const silentEvents = ['get-pinning-volume-analysis', 'get-whole-candle-analysis', 'broker:perps-sizing'];
    if (!silentEvents.includes(event)) {
      console.log(`📤 [WS EMIT] ${event}`, data);
    }
    this.socket.emit(event, data);
  }

  /**
   * Listen for a specific event from the server
   */
  on(event: string, callback: (...args: any[]) => void): void {
    if (!this.socket) {
      console.warn(`⚠️ Cannot listen for ${event}: socket not initialized`);
      return;
    }
    this.socket.on(event, callback);
  }

  /**
   * Emit connection state changes for UI feedback
   */
  private emitConnectionState(
    state: 'connected' | 'disconnected' | 'reconnecting' | 'failed',
    reason?: string,
    attempt?: number
  ): void {
    const stateData: any = { state };

    if (reason) {
      stateData.reason = reason;
    }

    if (attempt !== undefined) {
      stateData.attempt = attempt;
      stateData.maxAttempts = this.maxReconnectAttempts;
    }

    // Emit via Socket.IO if connected
    if (this.socket) {
      this.socket.emit('connection-state', stateData);
    }

    // Also log for debugging
    log.debug('Connection state changed', stateData);
  }

  /**
   * Remove a specific event listener
   */
  off(event: string, callback?: (...args: any[]) => void): void {
    if (!this.socket) {
      console.warn(`⚠️ Cannot remove listener for ${event}: socket not initialized`);
      return;
    }
    if (callback) {
      this.socket.off(event, callback);
    } else {
      // Remove all listeners for this event
      this.socket.off(event);
    }
  }

  /**
   * Switch to backtest mode
   */
  switchToBacktestMode(symbol: string, startDate: Date, endDate: Date, initialSeekTimestamp?: number): void {
    console.log(`📊 Switching to backtest mode for ${symbol}...`);
    if (initialSeekTimestamp) {
      console.log(`🎯 Initial seek timestamp:`, new Date(initialSeekTimestamp).toISOString());
    }

    // Store the seek timestamp for use after data loads
    this.pendingSeekTimestamp = initialSeekTimestamp;

    // Unsubscribe from live data
    this.unsubscribe(symbol);

    // Update store
    const marketStore = useMarketStore.getState();
    marketStore.setDataMode('backtest');
    marketStore.setBacktestLoading(true);
    marketStore.setCandles([]);

    // Request backtest data
    this.requestBacktestData(symbol, startDate, endDate);
  }
}

// Create singleton instance
export const wsService = new WebSocketService();
